import type { JobPipelineTypeSchema, SettingsTypeEnumSchema, SoftwareEnumSchema } from "@domain/schemas/BI";

/**
 * The generated BI enums live in an ambient `.d.ts`, so they carry no runtime
 * value. This module is imported at store-initialisation time, so it keys off
 * the string literals (which are the wire values) and only uses the enums as
 * types. Callers keep working with the enum types.
 */
type SoftwareKey = `${SoftwareEnumSchema}`;
type PipelineKey = `${JobPipelineTypeSchema}`;
type SettingsKey = `${SettingsTypeEnumSchema}`;

/**
 * Which pipelines a search engine offers.
 *
 * `options` is the full set the engine supports, in display order. An engine with
 * a single option does not show the selector at all, because there is nothing to
 * choose. SpectroMine has no library-generation stage, so it offers its Pulsar
 * search and library based only.
 */
export type PipelineCapability = {
  options: PipelineOptionDescriptor[];
  defaultPipelineType: JobPipelineTypeSchema;
};

/**
 * One selectable pipeline. The label is per engine because the same underlying
 * pipeline is named differently: Spectronaut calls it directDIA, SpectroMine
 * calls it Pulsar search.
 */
export interface PipelineOptionDescriptor {
  pipelineType: JobPipelineTypeSchema;
  label: string;
}

/**
Per-engine description of how the job setup form should be composed.
*/
export interface SoftwareProfile {
  software: SoftwareEnumSchema;
  pipeline: PipelineCapability;
  settingsSections: SettingsSectionDescriptor[];
  accent: SoftwareAccent;
  /**
   * Display names for steps the engine names differently. Steps are stored under
   * shared enum values, so SpectroMine's single search step is persisted as
   * `PulsarStep1` even though there is no step 2 or 3 to distinguish it from.
   */
  stepLabels: Partial<Record<string, string>>;
  /**
   * Whether the engine accepts an isobaric labelling method. Only SpectroMine has a
   * `-q` flag; the backend rejects the field for any other engine.
   */
  supportsQuantification: boolean;
}

/**
 * Selection colours used to tell the search engines apart at a glance.
 *
 * Kept away from the amber/red families, which this app reserves for warnings
 * and errors. Both pairings meet WCAG AA contrast for text on background.
 */
export interface SoftwareAccent {
  background: string;
  border: string;
  text: string;
}

/**
 * A settings picker in the job setup form. Ordering here is the render order.
 *
 * `label` is per engine on purpose: "directDIA Settings" is Spectronaut
 * vocabulary and must not be shown for another search engine.
 */
export interface SettingsSectionDescriptor {
  settingsType: SettingsTypeEnumSchema;
  label: string;
  /**
  Rendered as a `title` tooltip on the legend when present.
  */
  tooltip?: string;
  /**
  Single-select sections replace the current selection instead of appending.
  */
  selection: "single" | "multi";
  /**
  Sections behind the "Advanced Settings" disclosure.
  */
  advanced: boolean;
  /**
   * Whether the files belong to one search engine. GO annotations and custom
   * modification repositories are plain reference data with no engine-specific
   * format, so they are shared: the list is not filtered by engine and files
   * carry no engine attribution.
   */
  softwareScoped: boolean;
  /**
  Pipelines for which this section does not apply.
  */
  hiddenForPipelineTypes?: JobPipelineTypeSchema[];
}

const asSoftware = (value: SoftwareKey) => value as SoftwareEnumSchema;
const asPipeline = (value: PipelineKey) => value as JobPipelineTypeSchema;
const asSettings = (value: SettingsKey) => value as SettingsTypeEnumSchema;

/**
 * The five settings kinds both engines share. Engine-specific kinds are passed in
 * via `extraSections` rather than being listed here, so that a section which only
 * one engine understands can never leak into another engine's form.
 *
 * Wording still differs per engine, hence the experiment label/tooltip parameters.
 */
const buildSettingsSections = (experimentLabel: string, experimentTooltip: string, extraSections: SettingsSectionDescriptor[] = []): SettingsSectionDescriptor[] => [
  {
    settingsType: asSettings("Experiment"),
    label: experimentLabel,
    tooltip: experimentTooltip,
    selection: "single",
    advanced: false,
    softwareScoped: true,
  },
  ...extraSections,
  {
    settingsType: asSettings("Report"),
    label: "Report Schema",
    selection: "multi",
    advanced: false,
    softwareScoped: true,
    hiddenForPipelineTypes: [asPipeline("LibraryGeneration")],
  },
  {
    settingsType: asSettings("GO"),
    label: "GO Annotation",
    selection: "multi",
    advanced: true,
    softwareScoped: false,
  },
  {
    settingsType: asSettings("Modification"),
    label: "Custom Modification Repository",
    selection: "single",
    advanced: true,
    softwareScoped: false,
  },
  {
    settingsType: asSettings("ConditionSetup"),
    label: "Condition setup",
    selection: "single",
    advanced: true,
    softwareScoped: true,
    hiddenForPipelineTypes: [asPipeline("LibraryGeneration")],
  },
];

const SPECTRONAUT_PROFILE: SoftwareProfile = {
  software: asSoftware("Spectronaut"),
  pipeline: {
    options: [
      { pipelineType: asPipeline("DirectDIA"), label: "directDIA®" },
      { pipelineType: asPipeline("LibraryGeneration"), label: "Library Generation" },
      { pipelineType: asPipeline("LibraryBased"), label: "Library Based Analysis" },
    ],
    defaultPipelineType: asPipeline("DirectDIA"),
  },
  settingsSections: buildSettingsSections("directDIA Settings", "For a given pipeline, only relevant settings from the directDIA setting schema will be applied"),
  accent: { background: "#dbeafe", border: "#93c5fd", text: "#1e3a8a" },
  stepLabels: {},
  supportsQuantification: false,
};

const SOFTWARE_PROFILES: Partial<Record<SoftwareKey, SoftwareProfile>> = {
  Spectronaut: SPECTRONAUT_PROFILE,
  SpectroMine: {
    software: asSoftware("SpectroMine"),
    pipeline: {
      options: [
        { pipelineType: asPipeline("DirectDIA"), label: "Pulsar search" },
        { pipelineType: asPipeline("LibraryBased"), label: "Library Based Analysis" },
      ],
      defaultPipelineType: asPipeline("DirectDIA"),
    },
    settingsSections: buildSettingsSections("Experiment Settings", "Only settings relevant to the SpectroMine search will be applied", [
      {
        // SpectroMine only. Emitted as -rs, additional to the experiment settings
        // under -s. Note that -rs means the report schema in Spectronaut, which is
        // why this section exists for SpectroMine alone.
        settingsType: asSettings("SpectroMineSearch"),
        label: "Search Settings",
        tooltip: "SpectroMine search settings, applied in addition to the experiment settings",
        selection: "single",
        advanced: false,
        softwareScoped: true,
      },
    ]),
    accent: { background: "#ede9fe", border: "#c4b5fd", text: "#5b21b6" },
    // SpectroMine runs a single Pulsar search, so the "Step 1" suffix is meaningless.
    stepLabels: { PulsarStep1: "Pulsar" },
    supportsQuantification: true,
  },
};

/**
Falls back to the Spectronaut profile for engines that are not wired up yet.
*/
export const getSoftwareProfile = (software?: SoftwareEnumSchema | null): SoftwareProfile => (software && SOFTWARE_PROFILES[software as SoftwareKey]) || SPECTRONAUT_PROFILE;

/**
The pipelines an engine supports, in display order, with their engine-specific labels.
*/
export const getPipelineOptions = (software?: SoftwareEnumSchema | null): PipelineOptionDescriptor[] => getSoftwareProfile(software).pipeline.options;

/**
Whether the engine has more than one pipeline, and so needs a selector.
*/
export const hasPipelineChoice = (software?: SoftwareEnumSchema | null): boolean => getPipelineOptions(software).length > 1;

/**
Whether the engine accepts an isobaric labelling method, and so needs a quantification selector.
*/
export const hasQuantificationChoice = (software?: SoftwareEnumSchema | null): boolean => getSoftwareProfile(software).supportsQuantification;

/**
 * The engine's own name for a pipeline, or `null` when the engine does not offer it
 * (for example the archive variant of library generation, which is never selected
 * directly). Callers fall back to the generic formatting in that case.
 */
export const getPipelineLabel = (software: SoftwareEnumSchema | null | undefined, pipelineType: JobPipelineTypeSchema | null | undefined): string | null =>
  pipelineType ? (getPipelineOptions(software).find((option) => option.pipelineType === pipelineType)?.label ?? null) : null;

/**
 * Corrects a pipeline that the given engine does not support, so switching engines
 * cannot leave a selection the backend would reject or ignore.
 */
export const resolvePipelineType = (software: SoftwareEnumSchema | null | undefined, pipelineType: JobPipelineTypeSchema | null | undefined): JobPipelineTypeSchema => {
  const { options, defaultPipelineType } = getSoftwareProfile(software).pipeline;
  return pipelineType && options.some((option) => option.pipelineType === pipelineType) ? pipelineType : defaultPipelineType;
};

/**
Selection colours for an engine, so the UI reads differently per search engine.
*/
export const getSoftwareAccent = (software?: SoftwareEnumSchema | null): SoftwareAccent => getSoftwareProfile(software).accent;

/**
The descriptor for one settings type, or `null` when the engine does not use it.
*/
export const getSettingsSection = (software: SoftwareEnumSchema | null | undefined, settingsType: SettingsTypeEnumSchema): SettingsSectionDescriptor | null =>
  getSoftwareProfile(software).settingsSections.find((section) => section.settingsType === settingsType) ?? null;

/**
The engine's own name for a step, or `null` when it uses the shared name.
*/
export const getStepLabel = (software: SoftwareEnumSchema | null | undefined, stepType: string | null | undefined): string | null =>
  stepType ? (getSoftwareProfile(software).stepLabels[stepType] ?? null) : null;

/**
Sections to render for an engine, after applying the pipeline-specific exclusions.
*/
export const getVisibleSettingsSections = (software: SoftwareEnumSchema | null | undefined, pipelineType: JobPipelineTypeSchema | null | undefined, isAdvanced: boolean): SettingsSectionDescriptor[] =>
  getSoftwareProfile(software).settingsSections.filter((section) => section.advanced === isAdvanced && !(pipelineType && section.hiddenForPipelineTypes?.includes(pipelineType)));
